import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getCurrentUser, type CurrentUser } from "../api/currentUser";
import { ApiProblem } from "../api/httpClient";
import { getAuthenticationSession } from "./session";

export const ACTOR_CONTEXT_SOFT_TTL_MS = 60_000;

export type ActorContextState =
  | Readonly<{ status: "loading" }>
  | Readonly<{ status: "ready"; actor: CurrentUser; fetchedAt: number }>
  | Readonly<{ status: "refreshing"; actor: CurrentUser; fetchedAt: number }>
  | Readonly<{ status: "stale"; actor: CurrentUser; fetchedAt: number; requestId?: string }>
  | Readonly<{ status: "forbidden"; requestId?: string }>
  | Readonly<{ status: "error"; requestId?: string }>;

type ActorContextActions = Readonly<{
  revalidate(): void;
}>;

type ActorContextValue = Readonly<{
  state: ActorContextState;
  actions: ActorContextActions;
}>;

const ActorContext = createContext<ActorContextValue | null>(null);

type DataSnapshot = Readonly<{ actor: CurrentUser; fetchedAt: number }>;

function extractRequestId(error: unknown): string | undefined {
  if (error instanceof ApiProblem && typeof error.problem.requestId === "string") {
    return error.problem.requestId;
  }
  return undefined;
}

function withRequestId(
  state: ActorContextState & { status: "stale" | "forbidden" | "error" },
  requestId?: string,
): ActorContextState {
  return requestId ? { ...state, requestId } : state;
}

export function ActorContextProvider({ children }: { readonly children: ReactNode }) {
  const [state, setState] = useState<ActorContextState>({ status: "loading" });
  const inFlight = useRef<Promise<CurrentUser> | null>(null);
  const abortController = useRef<AbortController | null>(null);
  const dataRef = useRef<DataSnapshot | null>(null);
  const stateRef = useRef<ActorContextState>({ status: "loading" });

  const runFetch = (showRefresh: boolean) => {
    if (inFlight.current) return;
    const controller = new AbortController();
    abortController.current = controller;
    if (showRefresh && dataRef.current) {
      setState({ status: "refreshing", actor: dataRef.current.actor, fetchedAt: dataRef.current.fetchedAt });
    }
    const request = getCurrentUser(controller.signal);
    inFlight.current = request;
    request
      .then((actor) => {
        const snapshot = { actor, fetchedAt: Date.now() };
        dataRef.current = snapshot;
        setState({ status: "ready", ...snapshot });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        if (error instanceof ApiProblem && error.status === 403
            && error.problem.code === "IDENTITY_ACCOUNT_UNAVAILABLE") {
          setState(withRequestId({ status: "forbidden" }, extractRequestId(error)));
          return;
        }
        if (dataRef.current) {
          setState(withRequestId(
            { status: "stale", actor: dataRef.current.actor, fetchedAt: dataRef.current.fetchedAt },
            extractRequestId(error),
          ));
          return;
        }
        setState(withRequestId({ status: "error" }, extractRequestId(error)));
      })
      .finally(() => {
        inFlight.current = null;
      });
  };

  const load = useCallback(() => {
    runFetch(false);
  }, []);

  const revalidate = useCallback(() => {
    runFetch(true);
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    load();
    return () => {
      abortController.current?.abort();
      inFlight.current = null;
    };
  }, [load]);

  useEffect(() => {
    const session = getAuthenticationSession();
    return session.subscribeTokenRefresh(revalidate);
  }, [revalidate]);

  useEffect(() => {
    const maybeRevalidate = () => {
      const current = stateRef.current;
      if ((current.status === "ready" || current.status === "refreshing")
          && Date.now() - current.fetchedAt >= ACTOR_CONTEXT_SOFT_TTL_MS) {
        revalidate();
      }
    };
    window.addEventListener("focus", maybeRevalidate);
    document.addEventListener("visibilitychange", maybeRevalidate);
    return () => {
      window.removeEventListener("focus", maybeRevalidate);
      document.removeEventListener("visibilitychange", maybeRevalidate);
    };
  }, [revalidate]);

  const actions = useCallback<() => ActorContextActions>(() => ({ revalidate }), [revalidate]);
  const value: ActorContextValue = { state, actions: actions() };
  return <ActorContext.Provider value={value}>{children}</ActorContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalActorContext(): ActorContextState | null {
  const context = useContext(ActorContext);
  return context ? context.state : null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useActorContext(): ActorContextState {
  const context = useContext(ActorContext);
  if (!context) {
    throw new Error("useActorContext must be used within ActorContextProvider");
  }
  return context.state;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useActorContextActions(): ActorContextActions {
  const context = useContext(ActorContext);
  if (!context) {
    throw new Error("useActorContextActions must be used within ActorContextProvider");
  }
  return context.actions;
}